/**
 * @fileoverview Module for `src/components/ClickToAgree.js`.
 */

import React, { Component } from 'react';
import Modal from './Modal';
import Loading from './Loading';
import PartnerAgreementTerms from './documents/PartnerAgreementTerms';
import propTypes from 'prop-types';
import { getNextSignablePartner, signNextPartner, updateNextSignablePartner } from '../lib/unsignedPartners';

const SCROLL_RATIO_THRESHOLD = 0.99;

/**
 * Partner agreement notice and signing flow for eligible admins.
 */
class ClickToAgree extends Component {
  /**
   * Initializes modal visibility, spinner, and next-signable-partner state.
   *
   * @param {Object} props - Component props.
   * @param {Object} props.auth - Auth client for partner agreement signing.
   */
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      showSpinner: false,
      agreementModalTitle: '',
      nextSignablePartner: getNextSignablePartner(),
    };
  }

  /**
   * Renders the unsigned-agreement banner and signing modal, or null
   * when no signable partner exists.
   *
   * @returns {JSX.Element|null} The agreement banner and modal.
   */
  render() {
    if (!this.state.nextSignablePartner || !this.state.nextSignablePartner.slug || this.state.nextSignablePartner.slug === '') {
      return null;
    }
    return (
      <div>
        <div className='announcement-banner partner-terms'>
          <p>
            <span>
              One or more Partners you administer have unsigned Partner Agreements.
            </span>
            { ' ' }
            <a
              className='fw-bold btn-link-underline'
              onClick={ this.showModal }
            >
              View and sign Partner Agreements.
            </a>
          </p>
        </div>
        <Modal
          hideModal={ this.hideModal }
          modalBlockContentJsx={
            (this.state.nextSignablePartner && !this.state.showSpinner)
              ? this.generateTerms(this.state.nextSignablePartner.name)
              : (<Loading />)
          }
          modalButtons={ this.generateModalButtons() }
          modalClassName='partner-terms'
          modalTitle={ this.state.agreementModalTitle }
          onBodyScroll={ this.onBodyScroll }
          showModal={ this.state.showModal }
        />
      </div>
    );
  }

  /**
   * Opens the agreement modal and refreshes signable partner state.
   *
   * @returns {Promise<void>} Resolves after modal state is prepared.
   */
  showModal = async () => {
    this.setState({
      agreementModalTitle: 'Checking for unsigned Partner Agreements...',
      showModal: true,
      showSpinner: true,
    });
    // Need to retrieve the next unsigned partner at runtime,
    // because another admin may have already signed the agreement after this admin logged in.
    await updateNextSignablePartner(this.props.auth);
    const nextSignablePartner = getNextSignablePartner();
    this.setState({
      agreementModalTitle: `Partner Agreement: ${ nextSignablePartner ? nextSignablePartner.name : '' }`,
      nextSignablePartner,
      showSpinner: false,
    });
  };

  /**
   * Builds the partner agreement terms component for the modal body.
   *
   * @param {string} partnerName - Display name of the partner.
   * @returns {JSX.Element} The partner agreement terms content.
   */
  generateTerms = (partnerName) => (
    <PartnerAgreementTerms
      partnerName={ partnerName }
      userFullName={ this.props.auth.getUserName() }
    />
  );

  /**
   * Closes the agreement modal and resets the scroll-reached flag.
   */
  hideModal = (() => {
    this.setState({ agreementBottomReached: false, showModal: false });
  });

  /**
   * Tracks modal body scroll position and enables the "I Agree" button
   * once the user scrolls to the bottom.
   *
   * @param {Event} event - The scroll event from the modal body.
   */
  onBodyScroll = ((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const scrollRatio = scrollTop / (scrollHeight - clientHeight);

    if (scrollRatio >= SCROLL_RATIO_THRESHOLD) {
      this.setState({ agreementBottomReached: true });
    }
  });

  /**
   * Builds the modal footer with Close and I Agree buttons.
   *
   * @returns {JSX.Element} The modal action buttons.
   */
  generateModalButtons = () => (
    <div style={{ width: '100%' }}>
      <button
        className='btn btn-secondary'
        data-dismiss='modal'
        onClick={ this.hideModal }
        style={{ float: 'left' }}
        type='button'
      >
        Close
      </button>

      <div style={{ float: 'right' }} >
        <button
          className='btn btn-secondary'
          data-dismiss='modal'
          disabled={ this.state.showSpinner || !this.state.nextSignablePartner || !this.state.agreementBottomReached }
          onClick={ this.signAgreement }
          type='button'
        >
          I Agree
        </button>
      </div>
    </div>
  );

  /**
   * Signs the active partner agreement and advances to the next pending one.
   *
   * @returns {Promise<void>} Resolves after signing state updates.
   */
  signAgreement = async () => {
    this.setState({ showSpinner: true, agreementModalTitle: 'Signing Partner Agreement...' });
    await signNextPartner(this.props.auth);
    this.setState({ agreementBottomReached: false, showModal: false, showSpinner: false, nextSignablePartner: getNextSignablePartner() });
  };
}

ClickToAgree.propTypes = {
  auth: propTypes.object,
};

export default ClickToAgree;
